import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchAuthSession, signIn, signInWithRedirect } from 'aws-amplify/auth';
import { isAmplifyAuthConfigured } from '../config/amplifyAuth';
import {
  recordErrorStage,
  recordFunnelStep,
  recordLatency,
  recordPageView,
  recordUserAction,
} from '../utils/rum';
import {
  buildOnboardingDestination,
  buildOnboardingSearch,
  finalizeOnboardingAfterAuth,
  persistV2OnboardingState,
  resolveV2OnboardingState,
} from '../utils/v2Onboarding';
import { hasOnboardingState, onboardingRumContext, recordV2FunnelStep } from '../utils/v2Rum';

interface LoginProps {
  onLogin: (username: string, redirectTo?: string) => void;
}

export const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasTrackedPageViewRef = useRef(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const onboardingState = resolveV2OnboardingState(location.search);
  const nextDestination = buildOnboardingDestination(onboardingState);
  const nextSearch = buildOnboardingSearch(onboardingState);
  const isPendingWorkspaceCreate = Boolean(onboardingState.pendingCreate);

  useEffect(() => {
    if (hasTrackedPageViewRef.current) {
      return;
    }
    hasTrackedPageViewRef.current = true;
    recordPageView('login_page', {
      route: '/login',
      ...onboardingRumContext(onboardingState),
    });
    if (hasOnboardingState(onboardingState)) {
      recordV2FunnelStep('v2_onboarding_auth_resume', 'shown', onboardingRumContext(onboardingState));
    }
  }, [onboardingState]);

  // Check for existing session on component mount
  useEffect(() => {
    const checkExistingSession = async () => {
      const startedAt = performance.now();
      recordFunnelStep('login_existing_session_check', 'start');
      try {
        const session = await fetchAuthSession();
        const hasSession = Boolean(session.tokens?.idToken || session.tokens?.accessToken);
        if (hasSession) {
          recordFunnelStep('login_existing_session_check', 'success');
          onLogin('authenticated', nextDestination);
          finalizeOnboardingAfterAuth(onboardingState);
          navigate(nextDestination);
        } else {
          recordFunnelStep('login_existing_session_check', 'failure');
        }
      } catch (sessionError) {
        // No active session; user stays on login screen.
        recordFunnelStep('login_existing_session_check', 'failure');
        recordErrorStage('login_existing_session_check', sessionError);
      } finally {
        recordLatency('login_existing_session_check', performance.now() - startedAt);
      }
    };

    checkExistingSession();
  }, [navigate, nextDestination, onLogin, onboardingState]);

  const getFriendlyError = (authError: any): string => {
    const code = authError?.name || authError?.code;

    switch (code) {
      case 'NotAuthorizedException':
      case 'UserNotFoundException':
        return 'Invalid email/username or password.';
      case 'UserNotConfirmedException':
        return 'Your account is not confirmed yet. Please complete email verification.';
      case 'TooManyRequestsException':
      case 'LimitExceededException':
        return 'Too many attempts. Please wait and try again.';
      case 'NetworkError':
        return 'Network error. Please check your connection and retry.';
      default:
        return authError?.message || 'Sign in failed. Please try again.';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const startedAt = performance.now();
    setError('');
    recordFunnelStep('login_submit', 'start', {
      username,
      ...onboardingRumContext(onboardingState),
    });

    if (!isAmplifyAuthConfigured) {
      setError('Authentication is not configured yet. Please set Cognito environment variables.');
      recordFunnelStep('login_submit', 'blocked', {
        reason: 'auth_not_configured',
      });
      return;
    }

    setIsLoading(true);

    try {
      const result = await signIn({ username, password });

      if (result.isSignedIn) {
        recordFunnelStep('login_submit', 'success', {
          username,
          ...onboardingRumContext(onboardingState),
        });
        recordLatency('login_submit', performance.now() - startedAt);
        onLogin(username, nextDestination);
        finalizeOnboardingAfterAuth(onboardingState);
        navigate(nextDestination);
      } else {
        setError('Additional sign-in verification is required for this account.');
        recordFunnelStep('login_submit', 'failure', {
          reason: 'additional_verification_required',
        });
      }
    } catch (authError: any) {
      setError(getFriendlyError(authError));
      recordFunnelStep('login_submit', 'failure', {
        error_code: authError?.name || authError?.code || 'unknown',
      });
      recordErrorStage('login_submit', authError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    recordFunnelStep('google_login_redirect', 'start');

    if (!isAmplifyAuthConfigured) {
      setError('Authentication is not configured yet. Please set Cognito environment variables.');
      recordFunnelStep('google_login_redirect', 'blocked', {
        reason: 'auth_not_configured',
      });
      return;
    }

    setIsGoogleLoading(true);

    try {
      persistV2OnboardingState(onboardingState);
      await signInWithRedirect({ provider: 'Google' });
    } catch (authError: any) {
      setError(getFriendlyError(authError));
      setIsGoogleLoading(false);
      recordFunnelStep('google_login_redirect', 'failure');
      recordErrorStage('google_login_redirect', authError);
    }
  };

  return (
      <div className="min-h-screen bg-white text-[#101830]">
        <div className="grid min-h-screen lg:grid-cols-[560px_minmax(0,1fr)]">
          <aside className="flex flex-col justify-center bg-[#101830] px-8 py-12 text-white sm:px-12 lg:px-16">
            <div className="mx-auto w-full max-w-[432px]">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 rounded-md text-left transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-[#101830]"
              >
                <span className="h-3 w-3 rounded-full bg-[#1abf7d]" />
                <span className="text-[24px] font-bold tracking-[-0.03em]">Sela</span>
              </button>

              <div className="mt-20">
                <h1 className="max-w-[420px] text-[42px] font-bold leading-[1.18] tracking-[-0.05em] text-white">
                  Welcome back to your predevelopment workspace.
                </h1>
                <p className="mt-8 max-w-[420px] text-[17px] leading-8 text-[#99abc7]">
                  {isPendingWorkspaceCreate
                    ? 'Sign in to save your evaluation workspace and launch parcel research with the choices you already made.'
                    : 'Sign in to continue your predevelopment workspace and project setup.'}
                </p>
              </div>

              <div className="mt-16 rounded-2xl bg-[rgba(255,255,255,0.07)] px-6 py-5">
                <p className="text-[15px] leading-7 text-[#d9e0ed]">
                  {isPendingWorkspaceCreate
                    ? 'Your parcel, development paths, deal context, and priorities are saved. Sign in to create your workspace.'
                    : 'Resume parcel-aware setup and launch your predevelopment plan without losing progress.'}
                </p>
                <p className="mt-4 text-xs font-medium uppercase tracking-[0.12em] text-[#8099b8]">
                  V2 onboarding experience
                </p>
              </div>
            </div>
          </aside>

          <main className="flex items-center justify-center px-6 py-12 sm:px-10 lg:px-16">
            <div className="w-full max-w-[400px]">
              <h2 className="text-[40px] font-bold tracking-[-0.04em] text-[#101830]">Sign in</h2>
              <p className="mt-3 text-[15px] text-[#708099]">
                {isPendingWorkspaceCreate
                  ? 'Sign in to create your evaluation workspace.'
                  : 'Pick up right where you left off in the project onboarding flow.'}
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-4">
                <div>
                  <label htmlFor="username" className="mb-2 block text-[13px] font-medium text-[#26334d]">
                    Email or username
                  </label>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="h-11 w-full rounded-lg border border-[#d1d9e3] bg-[#f7fafc] px-3 text-sm text-[#26334d] placeholder:text-[#9eadc2] focus:border-[#101830] focus:outline-none focus:ring-2 focus:ring-[#101830]/10"
                    placeholder="Enter your email or username"
                    required
                    disabled={isLoading || isGoogleLoading}
                  />
                </div>

                <div>
                  <label htmlFor="password" className="mb-2 block text-[13px] font-medium text-[#26334d]">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 w-full rounded-lg border border-[#d1d9e3] bg-[#f7fafc] px-3 text-sm text-[#26334d] placeholder:text-[#9eadc2] focus:border-[#101830] focus:outline-none focus:ring-2 focus:ring-[#101830]/10"
                    placeholder="Enter your password"
                    required
                    disabled={isLoading || isGoogleLoading}
                  />
                </div>

                {error ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
                ) : null}

                <button
                  type="submit"
                  disabled={isLoading || isGoogleLoading || !username.trim() || !password.trim()}
                  className="flex h-12 w-full items-center justify-center rounded-lg bg-[#101830] px-4 text-[15px] font-semibold text-white transition-colors hover:bg-[#18233f] disabled:cursor-not-allowed disabled:bg-[#aeb8c8] focus:outline-none focus:ring-2 focus:ring-[#101830] focus:ring-offset-2"
                >
                  {isLoading ? 'Signing in...' : 'Sign in ->'}
                </button>
              </form>

              <div className="my-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-[#e4eaf1]" />
                <span className="text-sm text-[#99a6b8]">or</span>
                <div className="h-px flex-1 bg-[#e4eaf1]" />
              </div>

              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={isLoading || isGoogleLoading}
                className="flex h-11 w-full items-center justify-center gap-3 rounded-lg border border-[#d1d9e3] bg-white px-4 text-sm font-medium text-[#26334d] transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#101830] focus:ring-offset-2"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#ea4335] text-[10px] font-bold text-white">
                  G
                </span>
                <span>{isGoogleLoading ? 'Redirecting to Google...' : 'Continue with Google'}</span>
              </button>

              <div className="mt-6 text-center text-[13px]">
                <span className="text-[#808fa6]">Need an account? </span>
                <button
                  type="button"
                  onClick={() => {
                    recordUserAction('login_to_signup_click');
                    navigate(`/signup${nextSearch}`);
                  }}
                  className="font-medium text-[#101830] underline decoration-[#101830] underline-offset-2"
                  disabled={isLoading || isGoogleLoading}
                >
                  Sign up
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
  );
}; 